import type {
  BusinessCenterCompanyRecord,
  BusinessCenterRoleKey,
  BusinessCenterUserRecord,
} from "../../lib/types";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";
import { normalizeDefaultLandingWorkspace } from "../../lib/defaultLandingWorkspace";

export type CompanyUserRoleValue =
  | "company_admin"
  | "company_manager"
  | "company_supervisor"
  | "company_team_member"
  | "customer_client_user"
  | "viewer";

export interface CompanyUserRoleOption {
  key: CompanyUserRoleValue;
  label: string;
  description: string;
  permissionRole: BusinessCenterRoleKey;
}

export const companyUserRoleOptions: CompanyUserRoleOption[] = [
  {
    key: "company_admin",
    label: "Admin",
    description: "Can invite, deactivate, and manage company users.",
    permissionRole: "admin",
  },
  {
    key: "company_manager",
    label: "Manager",
    description: "Can manage assigned company records, reassign work, and review escalations.",
    permissionRole: "manager",
  },
  {
    key: "company_supervisor",
    label: "Supervisor",
    description: "Can oversee collector activity and review operational follow-up.",
    permissionRole: "supervisor",
  },
  {
    key: "company_team_member",
    label: "Collector / Team Member",
    description: "Can work assigned accounts, calls, notes, promises, letters, statements, and documents.",
    permissionRole: "team_member",
  },
  {
    key: "customer_client_user",
    label: "Client User",
    description: "Can only see company-scoped portal records assigned to them.",
    permissionRole: "client",
  },
  {
    key: "viewer",
    label: "Read Only",
    description: "Read-only access for oversight and review.",
    permissionRole: "viewer",
  },
];

export const companyStatusOptions: BusinessCenterUserRecord["status"][] = [
  "invited",
  "active",
  "deactivated",
  "archived",
];

export const portalAccessOptions: BusinessCenterUserRecord["portalAccessStatus"][] = [
  "pending",
  "active",
  "suspended",
  "revoked",
];

const companiesStore = createBusinessCenterOperationalStateStore<BusinessCenterCompanyRecord[]>({
  moduleKey: "business_center_companies",
  scope: { scopeKey: "company-directory", scopeType: "platform" },
  seed: createSeedCompanies,
  normalize: (companies) => companies.map(normalizeCompany),
  isEmpty: (companies) => !Array.isArray(companies) || companies.length === 0,
});

const usersStore = createBusinessCenterOperationalStateStore<BusinessCenterUserRecord[]>({
  moduleKey: "business_center_company_users",
  scope: { scopeType: "platform" },
  seed: createSeedUsers,
  normalize: (users) => users.map(normalizeUser),
  isEmpty: (users) => !Array.isArray(users) || users.length === 0,
});

export function loadBusinessCenterCompanies(): BusinessCenterCompanyRecord[] {
  return companiesStore.read();
}

export async function refreshBusinessCenterCompanies() {
  return companiesStore.refresh();
}

export function saveBusinessCenterCompanies(companies: BusinessCenterCompanyRecord[]) {
  return companiesStore.write(companies);
}

export function upsertBusinessCenterCompany(nextCompany: BusinessCenterCompanyRecord) {
  const current = loadBusinessCenterCompanies();
  const normalized = normalizeCompany({ ...nextCompany, updatedAt: new Date().toISOString() });
  const exists = current.some((company) => company.id === normalized.id);
  const companies = exists ? current.map((company) => (company.id === normalized.id ? normalized : company)) : [normalized, ...current];
  saveBusinessCenterCompanies(companies);
  return companies;
}

export function loadBusinessCenterCompanyUsers(): BusinessCenterUserRecord[] {
  return usersStore.read();
}

export async function refreshBusinessCenterCompanyUsers() {
  return usersStore.refresh();
}

export async function saveBusinessCenterCompanyUsers(users: BusinessCenterUserRecord[]) {
  return usersStore.writeAsync(users);
}

export async function upsertBusinessCenterCompanyUser(nextUser: BusinessCenterUserRecord) {
  return upsertBusinessCenterCompanyUsers([nextUser]);
}

export async function upsertBusinessCenterCompanyUsers(nextUsers: BusinessCenterUserRecord[]) {
  const users = [...loadBusinessCenterCompanyUsers()];

  for (const nextUser of nextUsers) {
    const normalized = normalizeUser({ ...nextUser, updatedAt: new Date().toISOString() });
    const normalizedEmail = normalizeUserEmail(normalized.email);
    const existingIndex = users.findIndex((user) =>
      user.id === normalized.id
      || (user.companyId === normalized.companyId && normalizeUserEmail(user.email) === normalizedEmail),
    );

    if (existingIndex >= 0) {
      const existing = users[existingIndex];
      users[existingIndex] = normalizeUser({
        ...existing,
        ...normalized,
        id: existing.id,
        createdAt: existing.createdAt,
      });
    } else {
      users.unshift(normalized);
    }
  }

  await saveBusinessCenterCompanyUsers(users);
  return users;
}

export async function updateBusinessCenterCompanyUser(userId: string, changes: Partial<BusinessCenterUserRecord>) {
  const current = loadBusinessCenterCompanyUsers();
  const users = current.map((user) =>
    user.id === userId
      ? normalizeUser({
          ...user,
          ...changes,
          updatedAt: new Date().toISOString(),
        })
      : user,
  );
  await saveBusinessCenterCompanyUsers(users);
  return users;
}

export async function removeBusinessCenterCompanyUser(userId: string) {
  const users = loadBusinessCenterCompanyUsers().filter((user) => user.id !== userId);
  await saveBusinessCenterCompanyUsers(users);
  return users;
}

export function mapCompanyRoleToPermissionRole(role: CompanyUserRoleValue | string): BusinessCenterRoleKey {
  return companyUserRoleOptions.find((option) => option.key === role)?.permissionRole || "viewer";
}

export function buildCompanyUserRoleSummary(users: BusinessCenterUserRecord[]) {
  return companyUserRoleOptions.map((option) => ({
    label: option.label,
    value: users.filter((user) => user.role === option.key).length,
    note: option.description,
    permissionRole: option.permissionRole,
  }));
}

function normalizeCompany(company: BusinessCenterCompanyRecord): BusinessCenterCompanyRecord {
  return {
    ...company,
    parentCompanyId: company.parentCompanyId || null,
    companyName: company.companyName || "Untitled Company",
    companySlug: company.companySlug || slugify(company.companyName || "untitled-company"),
    companyType:
      company.companyType === "parent" || company.companyType === "subsidiary" || company.companyType === "client_company"
        ? company.companyType
        : "client_company",
    brand: company.brand || "truevance_connect",
    status: company.status === "inactive" || company.status === "archived" ? company.status : "active",
    createdAt: company.createdAt || new Date().toISOString(),
    updatedAt: company.updatedAt || new Date().toISOString(),
  };
}

function normalizeUser(user: BusinessCenterUserRecord): BusinessCenterUserRecord {
  return {
    ...user,
    companyId: user.companyId || loadBusinessCenterCompanies()[0]?.id || "company-parent",
    parentCompanyId: user.parentCompanyId || loadBusinessCenterCompanies().find((company) => company.id === user.companyId)?.parentCompanyId || null,
    fullName: user.fullName || "Untitled User",
    firstName: user.firstName || deriveNamePart(user.fullName, "first"),
    lastName: user.lastName || deriveNamePart(user.fullName, "last"),
    displayName: user.displayName || user.fullName || "Untitled User",
    email: user.email || "user@example.com",
    jobTitle: user.jobTitle || "",
    department: user.department || "",
    phone: user.phone || "",
    team: user.team || "",
    notificationPreference: user.notificationPreference || "both",
    role:
      user.role === "company_admin" ||
      user.role === "company_manager" ||
      user.role === "company_supervisor" ||
      user.role === "company_team_member" ||
      user.role === "customer_client_user" ||
      user.role === "viewer"
        ? user.role
        : "viewer",
    status: user.status === "active" || user.status === "deactivated" || user.status === "archived" ? user.status : "invited",
    portalAccessStatus:
      user.portalAccessStatus === "active" ||
      user.portalAccessStatus === "suspended" ||
      user.portalAccessStatus === "revoked"
        ? user.portalAccessStatus
        : "pending",
    invitationStatus: user.invitationStatus || (user.status === "active" ? "accepted" : "pending"),
    invitationTokenHash: user.invitationTokenHash || "",
    invitationSentAt: user.invitationSentAt || "",
    invitationExpiresAt: user.invitationExpiresAt || "",
    invitationAcceptedAt: user.invitationAcceptedAt || "",
    invitationMessage: user.invitationMessage || "",
    lastLoginAt: user.lastLoginAt || "",
    defaultLandingWorkspace: normalizeDefaultLandingWorkspace(user.defaultLandingWorkspace),
    assignedClientIds: Array.isArray(user.assignedClientIds) ? user.assignedClientIds : [],
    assignedProjectIds: Array.isArray(user.assignedProjectIds) ? user.assignedProjectIds : [],
    assignedTaskIds: Array.isArray(user.assignedTaskIds) ? user.assignedTaskIds : [],
    assignedDocumentIds: Array.isArray(user.assignedDocumentIds) ? user.assignedDocumentIds : [],
    createdAt: user.createdAt || new Date().toISOString(),
    updatedAt: user.updatedAt || new Date().toISOString(),
  };
}

function createSeedCompanies(): BusinessCenterCompanyRecord[] {
  const now = new Date().toISOString();
  const parentId = "company-parent-business-center";
  return [
    normalizeCompany({
      id: parentId,
      companyName: "Business Center Parent Account",
      companySlug: "business-center-parent",
      companyType: "parent",
      brand: "truevance_connect",
      status: "active",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeCompany({
      id: "company-kreatethedate",
      parentCompanyId: parentId,
      companyName: "Kreatethedate",
      companySlug: "kreatethedate",
      companyType: "client_company",
      brand: "kreatethedate",
      status: "active",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeCompany({
      id: "company-truevance-connect",
      parentCompanyId: parentId,
      companyName: "Truevance Connect",
      companySlug: "truevance-connect",
      companyType: "client_company",
      brand: "truevance_connect",
      status: "active",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeCompany({
      id: "company-future-ar-recovery",
      parentCompanyId: parentId,
      companyName: "Future AR Recovery",
      companySlug: "future-ar-recovery",
      companyType: "subsidiary",
      brand: "truevance_connect",
      status: "active",
      createdAt: now,
      updatedAt: now,
    }),
    normalizeCompany({
      id: "company-future-businesses",
      parentCompanyId: parentId,
      companyName: "Future Businesses",
      companySlug: "future-businesses",
      companyType: "subsidiary",
      brand: "truevance_connect",
      status: "active",
      createdAt: now,
      updatedAt: now,
    }),
  ];
}

function createSeedUsers(): BusinessCenterUserRecord[] {
  const now = new Date().toISOString();
  return [
    normalizeUser({
      id: crypto.randomUUID(),
      companyId: "company-parent-business-center",
      parentCompanyId: null,
      firstName: "Business",
      lastName: "Admin",
      displayName: "Business Center Admin",
      fullName: "Business Center Admin",
      email: "admin@kreatethedate.com",
      jobTitle: "Platform Administrator",
      department: "Operations",
      phone: "",
      team: "Business Center OS",
      notificationPreference: "both",
      role: "company_admin",
      status: "active",
      portalAccessStatus: "active",
      defaultLandingWorkspace: "business-center-os",
      assignedClientIds: ["company-kreatethedate", "company-truevance-connect"],
      assignedProjectIds: [],
      assignedTaskIds: [],
      assignedDocumentIds: [],
      createdAt: now,
      updatedAt: now,
    }),
    normalizeUser({
      id: crypto.randomUUID(),
      companyId: "company-truevance-connect",
      parentCompanyId: "company-parent-business-center",
      firstName: "Operations",
      lastName: "Manager",
      displayName: "Operations Manager",
      fullName: "Operations Manager",
      email: "manager@truevanceconnect.com",
      jobTitle: "AR Manager",
      department: "Recovery Operations",
      phone: "",
      team: "AR Recovery",
      notificationPreference: "both",
      role: "company_manager",
      status: "active",
      portalAccessStatus: "active",
      defaultLandingWorkspace: "manager-command",
      assignedClientIds: ["company-truevance-connect"],
      assignedProjectIds: ["project-truevance-001"],
      assignedTaskIds: ["task-truevance-001"],
      assignedDocumentIds: ["doc-truevance-001"],
      createdAt: now,
      updatedAt: now,
    }),
    normalizeUser({
      id: crypto.randomUUID(),
      companyId: "company-truevance-connect",
      parentCompanyId: "company-parent-business-center",
      firstName: "Danielle",
      lastName: "Carter",
      displayName: "Danielle Carter",
      fullName: "Danielle Carter",
      email: "supervisor@truevanceconnect.com",
      jobTitle: "AR Supervisor",
      department: "Recovery Operations",
      phone: "",
      team: "AR Recovery",
      notificationPreference: "both",
      role: "company_supervisor",
      status: "active",
      portalAccessStatus: "active",
      defaultLandingWorkspace: "ar-recovery-os",
      assignedClientIds: ["company-truevance-connect"],
      assignedProjectIds: ["project-truevance-001"],
      assignedTaskIds: ["task-truevance-001"],
      assignedDocumentIds: ["doc-truevance-001"],
      createdAt: now,
      updatedAt: now,
    }),
    normalizeUser({
      id: crypto.randomUUID(),
      companyId: "company-truevance-connect",
      parentCompanyId: "company-parent-business-center",
      firstName: "Maya",
      lastName: "Reynolds",
      displayName: "Maya Reynolds",
      fullName: "Maya Reynolds",
      email: "team@truevanceconnect.com",
      jobTitle: "AR Collector",
      department: "Recovery Operations",
      phone: "",
      team: "AR Recovery",
      notificationPreference: "both",
      role: "company_team_member",
      status: "active",
      portalAccessStatus: "active",
      defaultLandingWorkspace: "recovery-workbench",
      assignedClientIds: ["company-truevance-connect"],
      assignedProjectIds: ["project-truevance-001"],
      assignedTaskIds: ["task-truevance-001", "task-truevance-002"],
      assignedDocumentIds: ["doc-truevance-001"],
      createdAt: now,
      updatedAt: now,
    }),
    normalizeUser({
      id: crypto.randomUUID(),
      companyId: "company-kreatethedate",
      parentCompanyId: "company-parent-business-center",
      firstName: "Client",
      lastName: "Contact",
      displayName: "Client Primary Contact",
      fullName: "Client Primary Contact",
      email: "client@example.com",
      jobTitle: "Client Contact",
      department: "Client Team",
      phone: "",
      team: "Client Portal",
      notificationPreference: "email",
      role: "customer_client_user",
      status: "active",
      portalAccessStatus: "active",
      defaultLandingWorkspace: "client-portal",
      assignedClientIds: ["company-kreatethedate"],
      assignedProjectIds: ["project-ktd-001"],
      assignedTaskIds: ["task-ktd-001"],
      assignedDocumentIds: ["doc-ktd-001"],
      createdAt: now,
      updatedAt: now,
    }),
    normalizeUser({
      id: crypto.randomUUID(),
      companyId: "company-kreatethedate",
      parentCompanyId: "company-parent-business-center",
      firstName: "Read Only",
      lastName: "Reviewer",
      displayName: "Read Only Reviewer",
      fullName: "Read Only Reviewer",
      email: "viewer@example.com",
      jobTitle: "Read Only Reviewer",
      department: "Operations",
      phone: "",
      team: "Oversight",
      notificationPreference: "in_app",
      role: "viewer",
      status: "invited",
      portalAccessStatus: "pending",
      assignedClientIds: ["company-kreatethedate"],
      assignedProjectIds: [],
      assignedTaskIds: [],
      assignedDocumentIds: [],
      createdAt: now,
      updatedAt: now,
    }),
  ];
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function deriveNamePart(fullName: string | undefined, part: "first" | "last") {
  const pieces = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (!pieces.length) return "";
  return part === "first" ? pieces[0] : pieces.slice(1).join(" ");
}

function normalizeUserEmail(email: string) {
  return email.trim().toLowerCase();
}
